import { useState } from "react";
import { Heart, ShieldCheck, Lock, Repeat, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Reveal, Overline } from "@/components/Reveal";
import { TIERS, ORG, DONATE_URL } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export default function Donate() {
  const { t } = useLang();
  const d = t.donate;
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState("");
  const [monthly, setMonthly] = useState(false);

  const amount = custom ? Number(custom) : selected;

  const donate = () => {
    const url = new URL(DONATE_URL);
    if (amount && amount > 0) {
      url.searchParams.set("amount", String(amount));
      if (monthly) url.searchParams.set("frequency", "monthly");
    } else if (monthly) {
      url.searchParams.set("frequency", "monthly");
    }
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <section className="bg-[#0A2540] text-white" data-testid="donate-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <Overline className="text-[#4ade80]">{d.heroOverline}</Overline>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">{d.heroTitle}</h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{d.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="donate-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10 items-start">
          {/* Donation form */}
          <Reveal className="lg:col-span-3">
            <Card className="bg-white border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
              {/* Frequency toggle */}
              <div className="flex items-center justify-between rounded-xl bg-[#F1F5F9] p-4">
                <div className="flex items-center gap-3">
                  <Repeat className="w-5 h-5 text-[#16A34A]" aria-hidden="true" />
                  <div>
                    <div className="font-semibold text-[#0A2540]">{d.monthlyTitle}</div>
                    <div className="text-sm text-[#475569]">{d.monthlyDesc}</div>
                  </div>
                </div>
                <Switch checked={monthly} onCheckedChange={setMonthly} data-testid="monthly-toggle" aria-label={d.monthlyAria} />
              </div>

              {/* Preset tiers */}
              <div className="mt-8">
                <Label className="text-[#0A2540] font-semibold">{d.chooseAmount}</Label>
                <div className="mt-3 grid sm:grid-cols-3 gap-4">
                  {TIERS.map((tier, i) => {
                    const active = !custom && selected === tier.amount;
                    return (
                      <button
                        key={tier.amount}
                        type="button"
                        onClick={() => { setSelected(tier.amount); setCustom(""); }}
                        data-testid={`tier-${tier.amount}-button`}
                        aria-pressed={active}
                        className={`text-left rounded-xl border-2 p-5 transition-all ${active ? "border-[#16A34A] bg-[#16A34A]/5 shadow-sm" : "border-[#E2E8F0] hover:border-[#16A34A]/50"}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-display font-extrabold text-2xl text-[#0A2540]">${tier.amount}</span>
                          {active && <CheckCircle2 className="w-5 h-5 text-[#16A34A]" aria-hidden="true" />}
                        </div>
                        <div className="mt-2 text-sm font-semibold text-[#16A34A]">{d.tiers[i].label}</div>
                        <div className="mt-1 text-xs text-[#475569] leading-snug">{d.tiers[i].desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom amount */}
              <div className="mt-6">
                <Label htmlFor="custom-amount" className="text-[#0A2540] font-semibold">{d.customLabel}</Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569] font-semibold">$</span>
                  <Input id="custom-amount" type="number" min="1" inputMode="numeric" placeholder={d.customPlaceholder} value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="custom-amount-input" className="pl-8 h-12" />
                </div>
              </div>

              <Button onClick={donate} data-testid="donate-submit-button" className="mt-8 w-full bg-[#E85D04] hover:bg-[#CC5202] text-white font-bold rounded-full h-14 text-base shadow-md hover:shadow-lg transition-all">
                <Heart className="w-5 h-5 mr-2" aria-hidden="true" />
                {d.give}{amount && amount > 0 ? ` $${amount}${monthly ? d.perMonth : ""}` : ""}
              </Button>

              <p className="mt-4 text-center text-sm text-[#475569]">{d.redirectNote}</p>
              <p className="mt-2 flex items-center justify-center gap-2 text-sm text-[#475569]">
                <Lock className="w-4 h-4 text-[#16A34A]" aria-hidden="true" /> {d.secure}
              </p>
            </Card>
          </Reveal>

          {/* Trust / disclosure */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="space-y-6">
              <Card className="bg-white border-[#E2E8F0] rounded-2xl p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-7 h-7 text-[#16A34A]" aria-hidden="true" />
                  <h2 className="font-display font-bold text-xl text-[#0A2540]">{d.trustTitle}</h2>
                </div>
                <ul className="mt-5 space-y-4 text-[#475569]">
                  {d.trust.map((item, i) => (
                    <li key={i} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" /> {item}</li>
                  ))}
                </ul>
              </Card>

              <Card className="bg-[#F1F5F9] border-[#E2E8F0] rounded-2xl p-7" data-testid="tax-disclosure">
                <div className="flex items-center gap-2 text-[#0A2540] font-semibold">
                  <Info className="w-5 h-5 text-[#16A34A]" aria-hidden="true" /> {d.disclosureTitle}
                </div>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{d.disclosureBody(ORG.name, ORG.ein)}</p>
              </Card>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
